import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/api';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { 
  FolderKanban, BarChart3, AlertCircle, FileText, Clock, Download, 
  Filter, Calendar, TrendingUp, CheckCircle, XCircle, RefreshCw,
  ChevronDown, ChevronUp, Table, PieChart, ArrowUpRight, Search,
  FileWarning, ClipboardList, AlertTriangle, Eye, CheckSquare, Scale, Shield
} from 'lucide-react';
import { WalkthroughTrigger } from '@/components/walkthrough';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'open', label: 'Open' },
  { value: 'notice_created', label: 'Notice Created' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'in_adjudication', label: 'In Adjudication' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'closed', label: 'Closed' },
];

const TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'events', label: 'Delay Events' },
  { value: 'notices', label: 'Notices' },
  { value: 'claims', label: 'Claims' },
  { value: 'rfis', label: 'RFIs' },
  { value: 'sis', label: 'Site Instructions' },
  { value: 'vos', label: 'Variation Orders' },
  { value: 'meetings', label: 'Meeting Minutes' },
  { value: 'shop_drawings', label: 'Shop Drawings' },
  { value: 'procurement', label: 'Procurement' },
  { value: 'irs', label: 'IRS' },
  { value: 'subcontractors', label: 'Subcontractors' },
  { value: 'drawings', label: 'Drawings' },
  { value: 'tasks', label: 'Tasks' },
  { value: 'disputes', label: 'Disputes' },
  { value: 'closeout', label: 'Close-Out' },
  { value: 'periodic_reports', label: 'Periodic Reports' },
  { value: 'site_diary', label: 'Site Diary' },
  { value: 'plant', label: 'Plant & Equipment' },
];

const STATUS_COLORS = {
  draft: 'bg-orange-100 text-orange-700 border-orange-200',
  open: 'bg-blue-100 text-blue-700 border-blue-200',
  notice_created: 'bg-blue-100 text-blue-700 border-blue-200',
  shared: 'bg-blue-100 text-blue-700 border-blue-200',
  submitted: 'bg-purple-100 text-purple-700 border-purple-200',
  in_adjudication: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  amendments_required: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
  closed: 'bg-slate-100 text-slate-700 border-slate-200',
  captured: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  awaiting_response: 'bg-amber-100 text-amber-700 border-amber-200',
  answered: 'bg-teal-100 text-teal-700 border-teal-200',
  potential_dispute: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  formal_dispute: 'bg-red-100 text-red-700 border-red-200',
  negotiation_active: 'bg-amber-100 text-amber-700 border-amber-200',
  forum_active: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  awaiting_decision: 'bg-purple-100 text-purple-700 border-purple-200',
  settled: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  in_progress: 'bg-blue-100 text-blue-700 border-blue-200',
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  active: 'bg-blue-100 text-blue-700 border-blue-200',
};

export default function ReportsPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [projectSummaries, setProjectSummaries] = useState({});
  const [rfiStats, setRfiStats] = useState({});
  const [siStats, setSiStats] = useState({});
  const [voStats, setVoStats] = useState({});
  const [meetingStats, setMeetingStats] = useState({});
  const [shopDrawingStats, setShopDrawingStats] = useState({});
  const [procurementStats, setProcurementStats] = useState({});
  const [irsStats, setIrsStats] = useState({});
  const [subcontractorStats, setSubcontractorStats] = useState({});
  const [drawingStats, setDrawingStats] = useState({});
  const [taskStats, setTaskStats] = useState({});
  const [disputeStats, setDisputeStats] = useState({});
  const [closeoutStats, setCloseoutStats] = useState({});
  const [periodicStats, setPeriodicStats] = useState({});
  const [diaryStats, setDiaryStats] = useState({});
  const [plantStats, setPlantStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();

  // Filters
  const [selectedProject, setSelectedProject] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      // For professional team members (consultants), only load their assigned projects
      // For admin and other roles, load all projects
      const userRole = user?.role?.toLowerCase();
      let projectsList = [];
      
      if (userRole?.startsWith('prof_') || userRole === 'professional_team') {
        // Use consultant endpoint to get only assigned projects
        const consultantRes = await api.get('/consultant/projects');
        projectsList = consultantRes.data.projects || [];
      } else {
        // Admin and other roles see all projects
        const projectsRes = await api.projects.getAll();
        projectsList = projectsRes.data;
      }
      
      setProjects(projectsList);

      const summaries = {};
      const rfiData = {};
      const siData = {};
      const voData = {};
      const meetingData = {};
      const shopDrawingData = {};
      const procurementData = {};
      const irsData = {};
      const subcontractorData = {};
      const drawingData = {};
      const taskData = {};
      const disputeData = {};
      const closeoutData = {};
      const periodicData = {};
      const diaryData = {};
      const plantData = {};
      
      for (const project of projectsList) {
        try {
          const [summaryRes, rfiRes, siRes] = await Promise.all([
            api.projects.getSummary(project.id),
            api.rfi.getByProject(project.id).catch(() => ({ data: { rfis: [], stats: {} } })),
            api.siteInstructions.getByProject(project.id).catch(() => ({ data: [] }))
          ]);
          
          summaries[project.id] = summaryRes.data;
          rfiData[project.id] = rfiRes.data;
          siData[project.id] = Array.isArray(siRes.data) ? siRes.data : [];

          // Fetch additional modules in parallel
          const [voRes, meetingRes, shopRes, procRes, irsRes, subRes, drawRes] = await Promise.all([
            api.get(`/variation-orders?project_id=${project.id}`).catch(() => ({ data: { items: [] } })),
            api.get(`/meeting-minutes?project_id=${project.id}`).catch(() => ({ data: { minutes: [] } })),
            api.get(`/projects/${project.id}/shop-drawings`).catch(() => ({ data: { packages: [] } })),
            api.get(`/projects/${project.id}/procurement`).catch(() => ({ data: { items: [] } })),
            api.get(`/projects/${project.id}/irs`).catch(() => ({ data: { items: [] } })),
            api.subcontractors.getAll(project.id).catch(() => ({ data: [] })),
            api.drawings.getByProject(project.id).catch(() => ({ data: { drawings: [] } }))
          ]);

          voData[project.id] = voRes.data?.items || voRes.data?.variation_orders || [];
          meetingData[project.id] = meetingRes.data?.minutes || meetingRes.data?.items || [];
          shopDrawingData[project.id] = shopRes.data?.packages || shopRes.data?.items || [];
          procurementData[project.id] = procRes.data?.items || [];
          irsData[project.id] = irsRes.data?.items || [];
          subcontractorData[project.id] = Array.isArray(subRes.data) ? subRes.data : (subRes.data?.subcontractors || []);
          drawingData[project.id] = drawRes.data?.drawings || [];

          // Fetch tasks, disputes, closeout, periodic reports, diary, plant data
          const [taskRes, disputeRes, closeoutRes, periodicRes, diaryRes, plantRes] = await Promise.all([
            api.get(`/tasks/projects/${project.id}`).catch(() => ({ data: { tasks: [] } })),
            api.get(`/disputes/projects/${project.id}/register`).catch(() => ({ data: { matters: [] } })),
            api.get(`/closeout/projects/${project.id}/dashboard`).catch(() => ({ data: { packages: [], defect_summary: { total: 0 } } })),
            api.get(`/periodic-reports/dashboard/${project.id}`).catch(() => ({ data: { total_cycles: 0 } })),
            api.get(`/diary/projects/${project.id}`).catch(() => ({ data: { diaries: [] } })),
            api.get(`/plant/register?project_id=${project.id}`).catch(() => ({ data: { items: [] } })),
          ]);

          taskData[project.id] = taskRes.data?.tasks || [];
          disputeData[project.id] = disputeRes.data?.matters || [];
          closeoutData[project.id] = {
            packages: closeoutRes.data?.packages || [],
            defects: closeoutRes.data?.defect_summary || { total: 0 },
            kpi: closeoutRes.data?.kpi || {},
          };
          periodicData[project.id] = periodicRes.data || { total_cycles: 0, reports_submitted: 0, reports_outstanding: 0, reports_overdue: 0 };
          diaryData[project.id] = diaryRes.data?.diaries || (Array.isArray(diaryRes.data) ? diaryRes.data : []);
          plantData[project.id] = plantRes.data?.items || (Array.isArray(plantRes.data) ? plantRes.data : []);
        } catch (error) {
          summaries[project.id] = { events: [], notices: [], claims: [] };
          rfiData[project.id] = { rfis: [], stats: {} };
          siData[project.id] = [];
          voData[project.id] = [];
          meetingData[project.id] = [];
          shopDrawingData[project.id] = [];
          procurementData[project.id] = [];
          irsData[project.id] = [];
          subcontractorData[project.id] = [];
          drawingData[project.id] = [];
          taskData[project.id] = [];
          disputeData[project.id] = [];
          closeoutData[project.id] = { packages: [], defects: { total: 0 }, kpi: {} };
          periodicData[project.id] = { total_cycles: 0, reports_submitted: 0, reports_outstanding: 0, reports_overdue: 0 };
          diaryData[project.id] = [];
          plantData[project.id] = [];
        }
      }
      
      setProjectSummaries(summaries);
      setRfiStats(rfiData);
      setSiStats(siData);
      setVoStats(voData);
      setMeetingStats(meetingData);
      setShopDrawingStats(shopDrawingData);
      setProcurementStats(procurementData);
      setIrsStats(irsData);
      setSubcontractorStats(subcontractorData);
      setDrawingStats(drawingData);
      setTaskStats(taskData);
      setDisputeStats(disputeData);
      setCloseoutStats(closeoutData);
      setPeriodicStats(periodicData);
      setDiaryStats(diaryData);
      setPlantStats(plantData);
    } catch (error) {
      toast.error('Failed to load reports data');
    } finally {
      setLoading(false);
    }
  };

  // Flatten all items for table view
  const allItems = useMemo(() => {
    const items = [];
    const filteredProjects = selectedProject === 'all' 
      ? projects 
      : projects.filter(p => p.id === selectedProject);

    filteredProjects.forEach(project => {
      const summary = projectSummaries[project.id] || { events: [], notices: [], claims: [] };
      const rfis = rfiStats[project.id]?.rfis || [];
      const sis = siStats[project.id] || [];
      const vos = voStats[project.id] || [];
      const meetings = meetingStats[project.id] || [];
      const shopDrawings = shopDrawingStats[project.id] || [];
      const procItems = procurementStats[project.id] || [];
      const irsItems = irsStats[project.id] || [];
      const subs = subcontractorStats[project.id] || [];
      const drawings = drawingStats[project.id] || [];
      const tasks = taskStats[project.id] || [];
      const disputes = disputeStats[project.id] || [];
      const closeout = closeoutStats[project.id] || { packages: [], defects: { total: 0 }, kpi: {} };

      // Events
      if (selectedType === 'all' || selectedType === 'events') {
        (summary.events || []).forEach(e => {
          items.push({
            type: 'Event', typeIcon: 'event', project: project.name, projectId: project.id,
            reference: e.event_type || 'N/A', title: e.description?.substring(0, 80) || 'No description',
            status: e.status, createdAt: e.created_at, id: e.id, link: `/events/${e.id}`
          });
        });
      }

      // Notices
      if (selectedType === 'all' || selectedType === 'notices') {
        (summary.notices || []).forEach(n => {
          items.push({
            type: 'Notice', typeIcon: 'notice', project: project.name, projectId: project.id,
            reference: n.id?.substring(0, 8) || 'N/A', title: 'Notice Document',
            status: n.status, createdAt: n.created_at, id: n.id, link: `/notices/${n.id}`
          });
        });
      }

      // Claims
      if (selectedType === 'all' || selectedType === 'claims') {
        (summary.claims || []).forEach(c => {
          items.push({
            type: 'Claim', typeIcon: 'claim', project: project.name, projectId: project.id,
            reference: c.id?.substring(0, 8) || 'N/A', title: `EOT Claim - ${c.impact_type || 'Unknown'}`,
            status: c.status, createdAt: c.created_at, id: c.id, link: `/claims/${c.id}`,
            approvedDays: c.approved_days, approvedAmount: c.approved_amount
          });
        });
      }

      // RFIs
      if (selectedType === 'all' || selectedType === 'rfis') {
        rfis.forEach(r => {
          items.push({
            type: 'RFI', typeIcon: 'rfi', project: project.name, projectId: project.id,
            reference: r.rfi_number || 'N/A', title: r.title || 'Untitled RFI',
            status: r.status, createdAt: r.date_created, id: r.id,
            link: `/projects/${project.id}/rfi/${r.id}`, discipline: r.discipline, priority: r.priority
          });
        });
      }

      // Site Instructions
      if (selectedType === 'all' || selectedType === 'sis') {
        sis.forEach(s => {
          items.push({
            type: 'SI', typeIcon: 'si', project: project.name, projectId: project.id,
            reference: s.si_number || 'N/A', title: s.title || 'Untitled SI',
            status: s.status, createdAt: s.created_at, id: s.id,
            link: `/projects/${project.id}/si/${s.id}`, discipline: s.discipline
          });
        });
      }

      // Variation Orders
      if (selectedType === 'all' || selectedType === 'vos') {
        vos.forEach(v => {
          items.push({
            type: 'VO', typeIcon: 'vo', project: project.name, projectId: project.id,
            reference: v.vo_number || 'N/A', title: v.title || v.description || 'Variation Order',
            status: v.status, createdAt: v.created_at, id: v.id,
            link: `/projects/${project.id}`
          });
        });
      }

      // Meeting Minutes
      if (selectedType === 'all' || selectedType === 'meetings') {
        meetings.forEach(m => {
          items.push({
            type: 'Meeting', typeIcon: 'meeting', project: project.name, projectId: project.id,
            reference: m.meeting_number || m.id?.substring(0, 8) || 'N/A',
            title: m.meeting_title || m.title || 'Meeting Minutes',
            status: m.status || 'Uploaded', createdAt: m.created_at || m.meeting_date, id: m.id,
            link: `/projects/${project.id}`
          });
        });
      }

      // Shop Drawings
      if (selectedType === 'all' || selectedType === 'shop_drawings') {
        shopDrawings.forEach(sd => {
          items.push({
            type: 'Shop Dwg', typeIcon: 'shop_drawing', project: project.name, projectId: project.id,
            reference: sd.submittal_number || sd.package_number || 'N/A',
            title: sd.package_name || sd.title || 'Shop Drawing',
            status: sd.status, createdAt: sd.created_at, id: sd.id,
            link: `/projects/${project.id}`
          });
        });
      }

      // Procurement
      if (selectedType === 'all' || selectedType === 'procurement') {
        procItems.forEach(p => {
          items.push({
            type: 'Procurement', typeIcon: 'procurement', project: project.name, projectId: project.id,
            reference: p.item_ref || p.id?.substring(0, 8) || 'N/A',
            title: p.package_name || p.description || 'Procurement Item',
            status: p.status || 'Not Started', createdAt: p.created_at, id: p.id,
            link: `/projects/${project.id}`
          });
        });
      }

      // IRS
      if (selectedType === 'all' || selectedType === 'irs') {
        irsItems.forEach(i => {
          items.push({
            type: 'IRS', typeIcon: 'irs', project: project.name, projectId: project.id,
            reference: i.id?.substring(0, 8) || 'N/A',
            title: i.activity_name || i.description || 'IRS Item',
            status: i.status || 'Pending', createdAt: i.created_at, id: i.id,
            link: `/projects/${project.id}`
          });
        });
      }

      // Subcontractors
      if (selectedType === 'all' || selectedType === 'subcontractors') {
        subs.forEach(s => {
          items.push({
            type: 'Subcon', typeIcon: 'subcontractor', project: project.name, projectId: project.id,
            reference: s.company_name || 'N/A', title: `${s.trade || 'Trade'} - ${s.company_name || 'Unknown'}`,
            status: s.status || 'Active', createdAt: s.created_at, id: s.id,
            link: `/projects/${project.id}`
          });
        });
      }

      // Drawings
      if (selectedType === 'all' || selectedType === 'drawings') {
        drawings.forEach(d => {
          items.push({
            type: 'Drawing', typeIcon: 'drawing', project: project.name, projectId: project.id,
            reference: d.drawing_number || 'N/A', title: d.drawing_title || d.title || 'Drawing',
            status: d.status || 'Active', createdAt: d.created_at, id: d.id,
            link: `/projects/${project.id}`
          });
        });
      }

      // Tasks
      if (selectedType === 'all' || selectedType === 'tasks') {
        tasks.forEach(t => {
          items.push({
            type: 'Task', typeIcon: 'task', project: project.name, projectId: project.id,
            reference: t.task_number || t.id?.substring(0, 8) || 'N/A',
            title: t.title || t.description || 'Task',
            status: t.status || 'pending', createdAt: t.created_at, id: t.id,
            link: '/tasks'
          });
        });
      }

      // Disputes
      if (selectedType === 'all' || selectedType === 'disputes') {
        disputes.forEach(d => {
          items.push({
            type: 'Dispute', typeIcon: 'dispute', project: project.name, projectId: project.id,
            reference: d.matter_ref || d.id?.substring(0, 8) || 'N/A',
            title: d.title || d.description || 'Dispute Matter',
            status: d.status || 'potential_dispute', createdAt: d.created_at, id: d.id,
            link: `/projects/${project.id}/disputes`
          });
        });
      }

      // Close-Out
      if (selectedType === 'all' || selectedType === 'closeout') {
        (closeout.packages || []).forEach(p => {
          items.push({
            type: 'Close-Out', typeIcon: 'closeout', project: project.name, projectId: project.id,
            reference: p.package_number || p.id?.substring(0, 8) || 'N/A',
            title: p.package_name || 'Close-Out Package',
            status: p.package_stage || p.status || 'active', createdAt: p.created_at, id: p.id,
            link: `/projects/${project.id}/closeout`
          });
        });
      }

      // Periodic Reports
      if (selectedType === 'all' || selectedType === 'periodic_reports') {
        const pr = periodicStats[project.id] || {};
        if (pr.total_cycles > 0 || pr.active_cycle) {
          items.push({
            type: 'Periodic', typeIcon: 'periodic', project: project.name, projectId: project.id,
            reference: `${pr.total_cycles || 0} cycles`,
            title: `Periodic Reports — ${pr.reports_submitted || 0} submitted, ${pr.reports_outstanding || 0} outstanding`,
            status: pr.reports_overdue > 0 ? 'overdue' : (pr.reports_outstanding > 0 ? 'open' : 'submitted'),
            createdAt: pr.active_cycle?.due_date || new Date().toISOString(), id: `periodic-${project.id}`,
            link: `/projects/${project.id}/periodic-reports`
          });
        }
      }

      // Site Diary
      if (selectedType === 'all' || selectedType === 'site_diary') {
        const diaries = diaryStats[project.id] || [];
        diaries.forEach(d => {
          items.push({
            type: 'Diary', typeIcon: 'diary', project: project.name, projectId: project.id,
            reference: d.date || d.diary_date || 'N/A',
            title: `Site Diary — ${d.date || d.diary_date || 'N/A'}`,
            status: d.status || 'draft', createdAt: d.created_at || d.date, id: d.id,
            link: `/projects/${project.id}/site-diary`
          });
        });
      }

      // Plant & Equipment
      if (selectedType === 'all' || selectedType === 'plant') {
        const plantItems = plantStats[project.id] || [];
        plantItems.forEach(p => {
          items.push({
            type: 'Plant', typeIcon: 'plant', project: project.name, projectId: project.id,
            reference: p.asset_number || p.name || 'N/A',
            title: `${p.name || 'Plant Item'} — ${p.category || p.type || ''}`,
            status: p.status || 'Active', createdAt: p.created_at, id: p.id,
            link: `/projects/${project.id}/plant`
          });
        });
      }
    });

    // Apply filters
    let filtered = items;

    if (selectedStatus !== 'all') {
      filtered = filtered.filter(item => item.status === selectedStatus);
    }

    if (dateFrom) {
      const from = new Date(dateFrom);
      filtered = filtered.filter(item => new Date(item.createdAt) >= from);
    }

    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      filtered = filtered.filter(item => new Date(item.createdAt) <= to);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        item.title?.toLowerCase().includes(term) ||
        item.reference?.toLowerCase().includes(term) ||
        item.project?.toLowerCase().includes(term)
      );
    }

    return filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [projects, projectSummaries, rfiStats, siStats, voStats, meetingStats, shopDrawingStats, procurementStats, irsStats, subcontractorStats, drawingStats, taskStats, disputeStats, closeoutStats, periodicStats, diaryStats, plantStats, selectedProject, selectedType, selectedStatus, dateFrom, dateTo, searchTerm]);

  // Calculate aggregated stats
  const stats = useMemo(() => {
    let totalEvents = 0, totalNotices = 0, totalClaims = 0, totalRfis = 0, totalSis = 0;
    let totalVos = 0, totalMeetings = 0, totalShopDrawings = 0, totalProcurement = 0;
    let totalIrs = 0, totalSubcontractors = 0, totalDrawings = 0;
    let totalTasks = 0, totalDisputes = 0, totalCloseout = 0;
    let totalPeriodic = 0, totalDiary = 0, totalPlant = 0;
    let approvedClaims = 0, pendingClaims = 0, rejectedClaims = 0;
    let overdueRfis = 0, overdueEvents = 0;
    let totalApprovedDays = 0, totalApprovedAmount = 0;

    const filteredProjects = selectedProject === 'all' 
      ? projects 
      : projects.filter(p => p.id === selectedProject);

    filteredProjects.forEach(project => {
      const summary = projectSummaries[project.id] || { events: [], notices: [], claims: [] };
      const rfis = rfiStats[project.id]?.rfis || [];
      const sis = siStats[project.id] || [];
      const vos = voStats[project.id] || [];
      const meetings = meetingStats[project.id] || [];
      const shopDrawings = shopDrawingStats[project.id] || [];
      const procItems = procurementStats[project.id] || [];
      const irsItems = irsStats[project.id] || [];
      const subs = subcontractorStats[project.id] || [];
      const drawings = drawingStats[project.id] || [];
      const tasks = taskStats[project.id] || [];
      const disputes = disputeStats[project.id] || [];
      const closeout = closeoutStats[project.id] || { packages: [], defects: { total: 0 }, kpi: {} };

      let events = summary.events || [];
      let notices = summary.notices || [];
      let claims = summary.claims || [];

      if (selectedStatus !== 'all') {
        events = events.filter(e => e.status === selectedStatus);
        notices = notices.filter(n => n.status === selectedStatus);
        claims = claims.filter(c => c.status === selectedStatus);
      }

      if (dateFrom) {
        const from = new Date(dateFrom);
        events = events.filter(e => new Date(e.created_at) >= from);
        notices = notices.filter(n => new Date(n.created_at) >= from);
        claims = claims.filter(c => new Date(c.created_at) >= from);
      }

      if (dateTo) {
        const to = new Date(dateTo);
        events = events.filter(e => new Date(e.created_at) <= to);
        notices = notices.filter(n => new Date(n.created_at) <= to);
        claims = claims.filter(c => new Date(c.created_at) <= to);
      }

      totalEvents += events.length;
      totalNotices += notices.length;
      totalClaims += claims.length;
      totalRfis += rfis.length;
      totalSis += sis.length;
      totalVos += vos.length;
      totalMeetings += meetings.length;
      totalShopDrawings += shopDrawings.length;
      totalProcurement += procItems.length;
      totalIrs += irsItems.length;
      totalSubcontractors += subs.length;
      totalDrawings += drawings.length;
      totalTasks += tasks.length;
      totalDisputes += disputes.length;
      totalCloseout += (closeout.packages || []).length;
      totalPeriodic += (periodicStats[project.id]?.total_cycles || 0);
      totalDiary += (diaryStats[project.id] || []).length;
      totalPlant += (plantStats[project.id] || []).length;

      const approved = claims.filter(c => c.status === 'approved');
      approvedClaims += approved.length;
      approved.forEach(c => {
        totalApprovedDays += c.approved_days || 0;
        totalApprovedAmount += c.approved_amount || 0;
      });
      
      pendingClaims += claims.filter(c => ['draft', 'submitted', 'in_adjudication'].includes(c.status)).length;
      rejectedClaims += claims.filter(c => c.status === 'rejected').length;
      
      overdueRfis += rfis.filter(r => r.aging_category === 'Overdue').length;
      overdueEvents += events.filter(e => e.status === 'draft' && 
        new Date(e.created_at) < new Date(Date.now() - 14 * 24 * 60 * 60 * 1000)).length;
    });

    return {
      totalEvents, totalNotices, totalClaims, totalRfis, totalSis,
      totalVos, totalMeetings, totalShopDrawings, totalProcurement,
      totalIrs, totalSubcontractors, totalDrawings,
      totalTasks, totalDisputes, totalCloseout,
      totalPeriodic, totalDiary, totalPlant,
      approvedClaims, pendingClaims, rejectedClaims,
      overdueRfis, overdueEvents,
      totalApprovedDays, totalApprovedAmount,
      totalItems: totalEvents + totalNotices + totalClaims + totalRfis + totalSis + totalVos + totalMeetings + totalShopDrawings + totalProcurement + totalIrs + totalSubcontractors + totalDrawings + totalTasks + totalDisputes + totalCloseout + totalPeriodic + totalDiary + totalPlant
    };
  }, [projects, projectSummaries, rfiStats, siStats, voStats, meetingStats, shopDrawingStats, procurementStats, irsStats, subcontractorStats, drawingStats, taskStats, disputeStats, closeoutStats, periodicStats, diaryStats, plantStats, selectedProject, selectedStatus, dateFrom, dateTo]);

  // Chart data for type distribution
  const chartData = useMemo(() => {
    const total = stats.totalItems;
    if (total === 0) return [];
    
    return [
      { label: 'Events', value: stats.totalEvents, color: '#f97316', percent: Math.round((stats.totalEvents / total) * 100) },
      { label: 'Notices', value: stats.totalNotices, color: '#3b82f6', percent: Math.round((stats.totalNotices / total) * 100) },
      { label: 'Claims', value: stats.totalClaims, color: '#8b5cf6', percent: Math.round((stats.totalClaims / total) * 100) },
      { label: 'RFIs', value: stats.totalRfis, color: '#06b6d4', percent: Math.round((stats.totalRfis / total) * 100) },
      { label: 'SIs', value: stats.totalSis, color: '#64748b', percent: Math.round((stats.totalSis / total) * 100) },
      { label: 'VOs', value: stats.totalVos, color: '#a855f7', percent: Math.round((stats.totalVos / total) * 100) },
      { label: 'Meetings', value: stats.totalMeetings, color: '#14b8a6', percent: Math.round((stats.totalMeetings / total) * 100) },
      { label: 'Shop Dwgs', value: stats.totalShopDrawings, color: '#6366f1', percent: Math.round((stats.totalShopDrawings / total) * 100) },
      { label: 'Procurement', value: stats.totalProcurement, color: '#10b981', percent: Math.round((stats.totalProcurement / total) * 100) },
      { label: 'IRS', value: stats.totalIrs, color: '#eab308', percent: Math.round((stats.totalIrs / total) * 100) },
      { label: 'Subcontractors', value: stats.totalSubcontractors, color: '#78716c', percent: Math.round((stats.totalSubcontractors / total) * 100) },
      { label: 'Drawings', value: stats.totalDrawings, color: '#0ea5e9', percent: Math.round((stats.totalDrawings / total) * 100) },
      { label: 'Tasks', value: stats.totalTasks, color: '#f43f5e', percent: Math.round((stats.totalTasks / total) * 100) },
      { label: 'Disputes', value: stats.totalDisputes, color: '#dc2626', percent: Math.round((stats.totalDisputes / total) * 100) },
      { label: 'Close-Out', value: stats.totalCloseout, color: '#059669', percent: Math.round((stats.totalCloseout / total) * 100) },
      { label: 'Periodic', value: stats.totalPeriodic, color: '#0284c7', percent: Math.round((stats.totalPeriodic / total) * 100) },
      { label: 'Diary', value: stats.totalDiary, color: '#16a34a', percent: Math.round((stats.totalDiary / total) * 100) },
      { label: 'Plant', value: stats.totalPlant, color: '#78716c', percent: Math.round((stats.totalPlant / total) * 100) },
    ].filter(d => d.value > 0);
  }, [stats]);

  const exportToCSV = async (type) => {
    setExporting(true);
    try {
      let data = [];
      let filename = '';
      const today = new Date().toISOString().split('T')[0];

      const filteredProjects = selectedProject === 'all' 
        ? projects 
        : projects.filter(p => p.id === selectedProject);

      if (type === 'events') {
        filename = `delay_events_report_${today}.csv`;
        data.push(['Project', 'Event Type', 'Description', 'Status', 'Start Date', 'End Date', 'Days Claimed', 'Responsible Party', 'Created At']);
        
        filteredProjects.forEach(project => {
          const summary = projectSummaries[project.id] || { events: [] };
          (summary.events || []).forEach(event => {
            data.push([
              project.name,
              event.event_type || '',
              (event.description || '').replace(/,/g, ';').substring(0, 200),
              event.status || '',
              event.start_date || '',
              event.end_date || '',
              event.days_claimed || '',
              event.responsible_party || '',
              event.created_at?.split('T')[0] || ''
            ]);
          });
        });
      } else if (type === 'notices') {
        filename = `notices_report_${today}.csv`;
        data.push(['Project', 'Notice ID', 'Status', 'Event ID', 'Shared At', 'Created At']);
        
        filteredProjects.forEach(project => {
          const summary = projectSummaries[project.id] || { notices: [] };
          (summary.notices || []).forEach(notice => {
            data.push([
              project.name,
              notice.id?.substring(0, 8) || '',
              notice.status || '',
              notice.event_id?.substring(0, 8) || '',
              notice.shared_at?.split('T')[0] || '',
              notice.created_at?.split('T')[0] || ''
            ]);
          });
        });
      } else if (type === 'claims') {
        filename = `eot_claims_report_${today}.csv`;
        data.push(['Project', 'Claim ID', 'Status', 'Impact Type', 'Approved Days', 'Approved Amount (R)', 'Status Note', 'Submitted At', 'Created At']);
        
        filteredProjects.forEach(project => {
          const summary = projectSummaries[project.id] || { claims: [] };
          (summary.claims || []).forEach(claim => {
            data.push([
              project.name,
              claim.id?.substring(0, 8) || '',
              claim.status || '',
              claim.impact_type || '',
              claim.approved_days || '',
              claim.approved_amount || '',
              (claim.status_note || '').replace(/,/g, ';'),
              claim.submitted_at?.split('T')[0] || '',
              claim.created_at?.split('T')[0] || ''
            ]);
          });
        });
      } else if (type === 'rfis') {
        filename = `rfi_report_${today}.csv`;
        data.push(['Project', 'RFI Number', 'Title', 'Discipline', 'Priority', 'Status', 'Days Outstanding', 'Response Days', 'Created At']);
        
        filteredProjects.forEach(project => {
          const rfis = rfiStats[project.id]?.rfis || [];
          rfis.forEach(rfi => {
            data.push([
              project.name,
              rfi.rfi_number || '',
              (rfi.title || '').replace(/,/g, ';'),
              rfi.discipline || '',
              rfi.priority || '',
              rfi.status || '',
              rfi.days_outstanding || '',
              rfi.response_days || '',
              rfi.date_created?.split('T')[0] || ''
            ]);
          });
        });
      } else if (type === 'sis') {
        filename = `site_instructions_report_${today}.csv`;
        data.push(['Project', 'SI Number', 'Title', 'Discipline', 'Status', 'Cost Impact', 'Time Impact', 'Issued Date', 'Created At']);
        
        filteredProjects.forEach(project => {
          const sis = siStats[project.id] || [];
          sis.forEach(si => {
            data.push([
              project.name,
              si.si_number || '',
              (si.title || '').replace(/,/g, ';'),
              si.discipline || '',
              si.status || '',
              si.cost_impact || '',
              si.time_impact || '',
              si.issued_date?.split('T')[0] || '',
              si.created_at?.split('T')[0] || ''
            ]);
          });
        });
      } else if (type === 'tasks') {
        filename = `tasks_report_${today}.csv`;
        data.push(['Project', 'Task Number', 'Title', 'Status', 'Priority', 'Assigned To', 'Due Date', 'Created At']);
        
        filteredProjects.forEach(project => {
          const tasks = taskStats[project.id] || [];
          tasks.forEach(t => {
            data.push([
              project.name,
              t.task_number || '',
              (t.title || t.description || '').replace(/,/g, ';'),
              t.status || '',
              t.priority || '',
              t.assigned_to_name || t.assigned_to || '',
              t.due_date?.split('T')[0] || '',
              t.created_at?.split('T')[0] || ''
            ]);
          });
        });
      } else if (type === 'disputes') {
        filename = `disputes_report_${today}.csv`;
        data.push(['Project', 'Matter Ref', 'Title', 'Status', 'Forum', 'Amount in Dispute', 'Risk Level', 'Created At']);
        
        filteredProjects.forEach(project => {
          const disputes = disputeStats[project.id] || [];
          disputes.forEach(d => {
            data.push([
              project.name,
              d.matter_ref || '',
              (d.title || d.description || '').replace(/,/g, ';'),
              d.status || '',
              d.current_forum || '',
              d.amount_in_dispute || '',
              d.risk_level || '',
              d.created_at?.split('T')[0] || ''
            ]);
          });
        });
      } else if (type === 'closeout') {
        filename = `closeout_report_${today}.csv`;
        data.push(['Project', 'Package Number', 'Package Name', 'Stage', 'Defects Open', 'Defects Closed', 'Readiness %', 'Created At']);
        
        filteredProjects.forEach(project => {
          const closeout = closeoutStats[project.id] || { packages: [] };
          (closeout.packages || []).forEach(p => {
            data.push([
              project.name,
              p.package_number || '',
              (p.package_name || '').replace(/,/g, ';'),
              p.package_stage || '',
              p.stats?.open_defects || 0,
              p.stats?.closed_defects || 0,
              p.stats?.readiness_pct || 0,
              p.created_at?.split('T')[0] || ''
            ]);
          });
        });
      } else {
        // All data
        filename = `full_report_${today}.csv`;
        data.push(['Project', 'Type', 'Reference', 'Title/Description', 'Status', 'Created At']);
        
        filteredProjects.forEach(project => {
          const summary = projectSummaries[project.id] || { events: [], notices: [], claims: [] };
          const rfis = rfiStats[project.id]?.rfis || [];
          const sis = siStats[project.id] || [];
          
          (summary.events || []).forEach(e => {
            data.push([project.name, 'Event', e.event_type, (e.description || '').substring(0, 100).replace(/,/g, ';'), e.status, e.created_at?.split('T')[0]]);
          });
          (summary.notices || []).forEach(n => {
            data.push([project.name, 'Notice', n.id?.substring(0, 8), 'Notice Document', n.status, n.created_at?.split('T')[0]]);
          });
          (summary.claims || []).forEach(c => {
            data.push([project.name, 'Claim', c.id?.substring(0, 8), `EOT Claim - ${c.impact_type || 'N/A'}`, c.status, c.created_at?.split('T')[0]]);
          });
          rfis.forEach(r => {
            data.push([project.name, 'RFI', r.rfi_number, (r.title || '').replace(/,/g, ';'), r.status, r.date_created?.split('T')[0]]);
          });
          sis.forEach(s => {
            data.push([project.name, 'SI', s.si_number, (s.title || '').replace(/,/g, ';'), s.status, s.created_at?.split('T')[0]]);
          });
        });
      }

      // Create and download CSV
      const csvContent = data.map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      
      toast.success(`Report exported: ${filename}`);
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setSelectedProject('all');
    setSelectedStatus('all');
    setSelectedType('all');
    setDateFrom('');
    setDateTo('');
    setSearchTerm('');
  };

  const getStatusBadge = (status) => {
    return STATUS_COLORS[status] || STATUS_COLORS.draft;
  };

  const getTypeColor = (type) => {
    const colors = {
      Event: 'bg-orange-50 text-orange-700 border-orange-200',
      Notice: 'bg-blue-50 text-blue-700 border-blue-200',
      Claim: 'bg-purple-50 text-purple-700 border-purple-200',
      RFI: 'bg-cyan-50 text-cyan-700 border-cyan-200',
      SI: 'bg-slate-50 text-slate-700 border-slate-200',
      VO: 'bg-violet-50 text-violet-700 border-violet-200',
      Meeting: 'bg-teal-50 text-teal-700 border-teal-200',
      'Shop Dwg': 'bg-indigo-50 text-indigo-700 border-indigo-200',
      Procurement: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      IRS: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      Subcon: 'bg-stone-50 text-stone-700 border-stone-200',
      Drawing: 'bg-sky-50 text-sky-700 border-sky-200',
      Task: 'bg-rose-50 text-rose-700 border-rose-200',
      Dispute: 'bg-red-50 text-red-700 border-red-200',
      'Close-Out': 'bg-green-50 text-green-700 border-green-200',
    };
    return colors[type] || colors.Event;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'R 0';
    return `R ${amount.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 text-slate-400 mx-auto mb-4 animate-spin" />
            <p className="text-slate-500 font-mono">Loading reports...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="reports-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-barlow font-bold uppercase tracking-tight mb-1">Reports & Analytics</h1>
            <p className="text-slate-500 text-sm">Consolidated view across all projects with filters and export</p>
          </div>
          <div className="flex gap-2 mt-4 md:mt-0">
            <WalkthroughTrigger moduleId="reports" />
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              size="sm"
              className="rounded-sm"
              data-testid="toggle-filters-btn"
            >
              <Filter className="h-4 w-4 mr-1" />
              {showFilters ? 'Hide' : 'Filters'}
            </Button>
            <Button
              onClick={() => loadAllData()}
              variant="outline"
              size="sm"
              className="rounded-sm"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4 mb-6" data-testid="filters-panel">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Project</Label>
                <Select value={selectedProject} onValueChange={setSelectedProject}>
                  <SelectTrigger className="rounded-sm h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Type</Label>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger className="rounded-sm h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Status</Label>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="rounded-sm h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">From</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="rounded-sm h-9"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">To</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="rounded-sm h-9"
                />
              </div>
              <div className="flex items-end">
                <Button onClick={clearFilters} variant="outline" size="sm" className="rounded-sm w-full h-9">
                  Clear
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-slate-100 p-1 rounded-sm">
            <TabsTrigger value="overview" className="rounded-sm data-[state=active]:bg-white">
              <BarChart3 className="h-4 w-4 mr-1.5" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="table" className="rounded-sm data-[state=active]:bg-white">
              <Table className="h-4 w-4 mr-1.5" />
              Data Table
            </TabsTrigger>
            <TabsTrigger value="export" className="rounded-sm data-[state=active]:bg-white">
              <Download className="h-4 w-4 mr-1.5" />
              Export
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Summary Stats Row - All 12 Modules */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <div className="bg-white border border-slate-200 rounded-sm p-3 sm:p-4 col-span-2 sm:col-span-3 md:col-span-4 lg:col-span-6">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Items Across All Modules</p>
                <p className="text-3xl font-barlow font-bold text-slate-900">{stats.totalItems}</p>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-sm p-3">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-orange-600">Events</p>
                <p className="text-2xl sm:text-3xl font-barlow font-bold text-orange-900">{stats.totalEvents}</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-blue-600">Notices</p>
                <p className="text-2xl sm:text-3xl font-barlow font-bold text-blue-900">{stats.totalNotices}</p>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-sm p-3">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-purple-600">Claims</p>
                <p className="text-2xl sm:text-3xl font-barlow font-bold text-purple-900">{stats.totalClaims}</p>
              </div>
              <div className="bg-cyan-50 border border-cyan-200 rounded-sm p-3">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-cyan-600">RFIs</p>
                <p className="text-2xl sm:text-3xl font-barlow font-bold text-cyan-900">{stats.totalRfis}</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-600">SIs</p>
                <p className="text-2xl sm:text-3xl font-barlow font-bold text-slate-900">{stats.totalSis}</p>
              </div>
              <div className="bg-violet-50 border border-violet-200 rounded-sm p-3">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-violet-600">VOs</p>
                <p className="text-2xl sm:text-3xl font-barlow font-bold text-violet-900">{stats.totalVos}</p>
              </div>
              <div className="bg-teal-50 border border-teal-200 rounded-sm p-3">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-teal-600">Meetings</p>
                <p className="text-2xl sm:text-3xl font-barlow font-bold text-teal-900">{stats.totalMeetings}</p>
              </div>
              <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-3">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-indigo-600">Shop Dwgs</p>
                <p className="text-2xl sm:text-3xl font-barlow font-bold text-indigo-900">{stats.totalShopDrawings}</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-emerald-600">Procurement</p>
                <p className="text-2xl sm:text-3xl font-barlow font-bold text-emerald-900">{stats.totalProcurement}</p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-3">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-yellow-600">IRS</p>
                <p className="text-2xl sm:text-3xl font-barlow font-bold text-yellow-900">{stats.totalIrs}</p>
              </div>
              <div className="bg-stone-50 border border-stone-200 rounded-sm p-3">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-stone-600">Subcons</p>
                <p className="text-2xl sm:text-3xl font-barlow font-bold text-stone-900">{stats.totalSubcontractors}</p>
              </div>
              <div className="bg-sky-50 border border-sky-200 rounded-sm p-3">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-sky-600">Drawings</p>
                <p className="text-2xl sm:text-3xl font-barlow font-bold text-sky-900">{stats.totalDrawings}</p>
              </div>
              <div className="bg-rose-50 border border-rose-200 rounded-sm p-3">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-rose-600">Tasks</p>
                <p className="text-2xl sm:text-3xl font-barlow font-bold text-rose-900">{stats.totalTasks}</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-sm p-3">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-red-600">Disputes</p>
                <p className="text-2xl sm:text-3xl font-barlow font-bold text-red-900">{stats.totalDisputes}</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-sm p-3">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-green-600">Close-Out</p>
                <p className="text-2xl sm:text-3xl font-barlow font-bold text-green-900">{stats.totalCloseout}</p>
              </div>
              <div className="bg-sky-50 border border-sky-200 rounded-sm p-3">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-sky-600">Periodic</p>
                <p className="text-2xl sm:text-3xl font-barlow font-bold text-sky-900">{stats.totalPeriodic}</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-emerald-600">Diary</p>
                <p className="text-2xl sm:text-3xl font-barlow font-bold text-emerald-900">{stats.totalDiary}</p>
              </div>
              <div className="bg-stone-50 border border-stone-200 rounded-sm p-3">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-stone-600">Plant</p>
                <p className="text-2xl sm:text-3xl font-barlow font-bold text-stone-900">{stats.totalPlant}</p>
              </div>
            </div>

            {/* Two Column Layout: Claims Stats + Chart */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Claims Status Breakdown */}
              <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-5">
                <h3 className="text-sm font-barlow font-bold uppercase mb-4 flex items-center gap-2 text-slate-700">
                  <ClipboardList className="h-4 w-4" />
                  Claims Analysis
                </h3>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-center">
                    <CheckCircle className="h-5 w-5 text-emerald-600 mx-auto mb-1" />
                    <p className="text-2xl font-barlow font-bold text-emerald-900">{stats.approvedClaims}</p>
                    <p className="text-xs text-emerald-600 font-medium">Approved</p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-center">
                    <Clock className="h-5 w-5 text-amber-600 mx-auto mb-1" />
                    <p className="text-2xl font-barlow font-bold text-amber-900">{stats.pendingClaims}</p>
                    <p className="text-xs text-amber-600 font-medium">Pending</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-center">
                    <XCircle className="h-5 w-5 text-red-600 mx-auto mb-1" />
                    <p className="text-2xl font-barlow font-bold text-red-900">{stats.rejectedClaims}</p>
                    <p className="text-xs text-red-600 font-medium">Rejected</p>
                  </div>
                </div>
                {stats.approvedClaims > 0 && (
                  <div className="border-t border-slate-100 pt-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Approved Totals</p>
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-lg font-barlow font-bold text-slate-900">{stats.totalApprovedDays}</span>
                        <span className="text-sm text-slate-500 ml-1">days</span>
                      </div>
                      <div className="text-right">
                        <span className="text-lg font-barlow font-bold text-emerald-700">{formatCurrency(stats.totalApprovedAmount)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Distribution Chart */}
              <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-5">
                <h3 className="text-sm font-barlow font-bold uppercase mb-4 flex items-center gap-2 text-slate-700">
                  <PieChart className="h-4 w-4" />
                  Distribution by Type
                </h3>
                {chartData.length > 0 ? (
                  <div className="space-y-3">
                    {chartData.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="w-20 text-xs font-medium text-slate-600">{item.label}</div>
                        <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${item.percent}%`, backgroundColor: item.color }}
                          />
                        </div>
                        <div className="w-12 text-right text-xs font-mono text-slate-600">{item.value}</div>
                        <div className="w-10 text-right text-xs font-mono text-slate-400">{item.percent}%</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <PieChart className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No data available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Alerts Row */}
            {(stats.overdueEvents > 0 || stats.overdueRfis > 0) && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <h3 className="text-sm font-barlow font-bold uppercase text-red-700">Attention Required</h3>
                </div>
                <div className="flex gap-6 text-sm">
                  {stats.overdueEvents > 0 && (
                    <span className="text-red-700">
                      <strong>{stats.overdueEvents}</strong> overdue event{stats.overdueEvents !== 1 ? 's' : ''} (draft &gt;14 days)
                    </span>
                  )}
                  {stats.overdueRfis > 0 && (
                    <span className="text-red-700">
                      <strong>{stats.overdueRfis}</strong> overdue RFI{stats.overdueRfis !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Project Breakdown */}
            <div className="bg-white border border-slate-200 rounded-sm shadow-sm">
              <div className="p-4 border-b border-slate-100">
                <h3 className="text-sm font-barlow font-bold uppercase text-slate-700">Project Breakdown</h3>
              </div>
              {projects.length === 0 ? (
                <div className="p-12 text-center">
                  <FolderKanban className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No projects found</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {(selectedProject === 'all' ? projects : projects.filter(p => p.id === selectedProject)).map((project) => {
                    const summary = projectSummaries[project.id] || { events: [], notices: [], claims: [] };
                    const rfis = rfiStats[project.id]?.rfis || [];
                    const sis = siStats[project.id] || [];
                    const vos = voStats[project.id] || [];
                    const meetings = meetingStats[project.id] || [];
                    const shopDrawings = shopDrawingStats[project.id] || [];
                    const procItems = procurementStats[project.id] || [];
                    const irsItems = irsStats[project.id] || [];
                    const subs = subcontractorStats[project.id] || [];
                    const drawings = drawingStats[project.id] || [];
                    const tasks = taskStats[project.id] || [];
                    const disputes = disputeStats[project.id] || [];
                    const closeoutPkgs = (closeoutStats[project.id]?.packages) || [];
                    const totalItems = (summary.events?.length || 0) + (summary.notices?.length || 0) + 
                                     (summary.claims?.length || 0) + rfis.length + sis.length +
                                     vos.length + meetings.length + shopDrawings.length +
                                     procItems.length + irsItems.length + subs.length + drawings.length +
                                     tasks.length + disputes.length + closeoutPkgs.length;

                    return (
                      <div
                        key={project.id}
                        className="p-4 hover:bg-slate-50 transition-colors cursor-pointer group"
                        onClick={() => navigate(`/projects/${project.id}`)}
                        data-testid={`project-card-${project.id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3 min-w-0">
                            <FolderKanban className="h-5 w-5 text-slate-400 shrink-0" />
                            <div className="min-w-0">
                              <h4 className="font-barlow font-bold uppercase text-slate-900 truncate">{project.name}</h4>
                              <p className="text-xs text-slate-500">{project.contract_type}</p>
                            </div>
                          </div>
                          <Badge variant="secondary" className="font-mono shrink-0 ml-2">{totalItems}</Badge>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-4 text-xs flex-wrap ml-8">
                          {(summary.events?.length || 0) > 0 && <span className="text-orange-600 font-medium">{summary.events.length} Events</span>}
                          {(summary.notices?.length || 0) > 0 && <span className="text-blue-600 font-medium">{summary.notices.length} Notices</span>}
                          {(summary.claims?.length || 0) > 0 && <span className="text-purple-600 font-medium">{summary.claims.length} Claims</span>}
                          {rfis.length > 0 && <span className="text-cyan-600 font-medium">{rfis.length} RFIs</span>}
                          {sis.length > 0 && <span className="text-slate-500 font-medium">{sis.length} SIs</span>}
                          {vos.length > 0 && <span className="text-violet-600 font-medium">{vos.length} VOs</span>}
                          {meetings.length > 0 && <span className="text-teal-600 font-medium">{meetings.length} Meetings</span>}
                          {shopDrawings.length > 0 && <span className="text-indigo-600 font-medium">{shopDrawings.length} Shop Dwgs</span>}
                          {procItems.length > 0 && <span className="text-emerald-600 font-medium">{procItems.length} Procurement</span>}
                          {irsItems.length > 0 && <span className="text-yellow-600 font-medium">{irsItems.length} IRS</span>}
                          {subs.length > 0 && <span className="text-stone-600 font-medium">{subs.length} Subcons</span>}
                          {drawings.length > 0 && <span className="text-sky-600 font-medium">{drawings.length} Drawings</span>}
                          {tasks.length > 0 && <span className="text-rose-600 font-medium">{tasks.length} Tasks</span>}
                          {disputes.length > 0 && <span className="text-red-600 font-medium">{disputes.length} Disputes</span>}
                          {closeoutPkgs.length > 0 && <span className="text-green-600 font-medium">{closeoutPkgs.length} Close-Out</span>}
                          {totalItems === 0 && <span className="text-slate-400">No items</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Data Table Tab */}
          <TabsContent value="table" className="space-y-4">
            {/* Search */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by title, reference, or project..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 rounded-sm"
                  data-testid="table-search-input"
                />
              </div>
              <Badge variant="secondary" className="font-mono">
                {allItems.length} item{allItems.length !== 1 ? 's' : ''}
              </Badge>
            </div>

            {/* Table */}
            <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="reports-data-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left p-3 font-bold uppercase text-xs tracking-wider text-slate-600">Type</th>
                      <th className="text-left p-3 font-bold uppercase text-xs tracking-wider text-slate-600">Reference</th>
                      <th className="text-left p-3 font-bold uppercase text-xs tracking-wider text-slate-600">Title</th>
                      <th className="text-left p-3 font-bold uppercase text-xs tracking-wider text-slate-600">Project</th>
                      <th className="text-left p-3 font-bold uppercase text-xs tracking-wider text-slate-600">Status</th>
                      <th className="text-left p-3 font-bold uppercase text-xs tracking-wider text-slate-600">Created</th>
                      <th className="text-center p-3 font-bold uppercase text-xs tracking-wider text-slate-600">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {allItems.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="p-12 text-center text-slate-400">
                          <FileWarning className="h-10 w-10 mx-auto mb-3 opacity-50" />
                          <p>No items match your filters</p>
                        </td>
                      </tr>
                    ) : (
                      allItems.slice(0, 100).map((item, idx) => (
                        <tr 
                          key={`${item.type}-${item.id}-${idx}`}
                          className="hover:bg-slate-50 transition-colors cursor-pointer"
                          onClick={() => navigate(item.link)}
                          data-testid={`table-row-${item.id}`}
                        >
                          <td className="p-3">
                            <Badge className={`${getTypeColor(item.type)} rounded-sm text-xs`}>
                              {item.type}
                            </Badge>
                          </td>
                          <td className="p-3 font-mono text-xs text-slate-600">{item.reference}</td>
                          <td className="p-3 text-slate-900 max-w-xs truncate">{item.title}</td>
                          <td className="p-3 text-slate-600">{item.project}</td>
                          <td className="p-3">
                            <Badge className={`${getStatusBadge(item.status)} rounded-sm text-xs capitalize`}>
                              {item.status?.replace(/_/g, ' ')}
                            </Badge>
                          </td>
                          <td className="p-3 text-slate-500 text-xs">{formatDate(item.createdAt)}</td>
                          <td className="p-3 text-center">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-7 w-7 p-0"
                              onClick={(e) => { e.stopPropagation(); navigate(item.link); }}
                            >
                              <Eye className="h-4 w-4 text-slate-400" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {allItems.length > 100 && (
                <div className="p-3 bg-slate-50 border-t border-slate-200 text-center text-xs text-slate-500">
                  Showing first 100 items. Use filters to narrow results or export for full data.
                </div>
              )}
            </div>
          </TabsContent>

          {/* Export Tab */}
          <TabsContent value="export" className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
              <h3 className="text-lg font-barlow font-bold uppercase mb-2 flex items-center gap-2">
                <Download className="h-5 w-5" />
                Export Reports
              </h3>
              <p className="text-sm text-slate-500 mb-6">Download project data as CSV files. Filters are applied to exports.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Button
                  onClick={() => exportToCSV('all')}
                  disabled={exporting}
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm h-auto py-4 flex-col items-start"
                  data-testid="export-all-btn"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Download className="h-4 w-4" />
                    <span className="font-bold">Full Report</span>
                  </div>
                  <span className="text-xs opacity-70">All types combined</span>
                </Button>
                
                <Button
                  onClick={() => exportToCSV('events')}
                  disabled={exporting}
                  variant="outline"
                  className="rounded-sm border-orange-300 text-orange-700 hover:bg-orange-50 h-auto py-4 flex-col items-start"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <AlertCircle className="h-4 w-4" />
                    <span className="font-bold">Delay Events</span>
                  </div>
                  <span className="text-xs opacity-70">{stats.totalEvents} items</span>
                </Button>

                <Button
                  onClick={() => exportToCSV('notices')}
                  disabled={exporting}
                  variant="outline"
                  className="rounded-sm border-blue-300 text-blue-700 hover:bg-blue-50 h-auto py-4 flex-col items-start"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="h-4 w-4" />
                    <span className="font-bold">Notices</span>
                  </div>
                  <span className="text-xs opacity-70">{stats.totalNotices} items</span>
                </Button>
                
                <Button
                  onClick={() => exportToCSV('claims')}
                  disabled={exporting}
                  variant="outline"
                  className="rounded-sm border-purple-300 text-purple-700 hover:bg-purple-50 h-auto py-4 flex-col items-start"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <ClipboardList className="h-4 w-4" />
                    <span className="font-bold">EOT Claims</span>
                  </div>
                  <span className="text-xs opacity-70">{stats.totalClaims} items</span>
                </Button>
                
                <Button
                  onClick={() => exportToCSV('rfis')}
                  disabled={exporting}
                  variant="outline"
                  className="rounded-sm border-cyan-300 text-cyan-700 hover:bg-cyan-50 h-auto py-4 flex-col items-start"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <FileWarning className="h-4 w-4" />
                    <span className="font-bold">RFIs</span>
                  </div>
                  <span className="text-xs opacity-70">{stats.totalRfis} items</span>
                </Button>
                
                <Button
                  onClick={() => exportToCSV('sis')}
                  disabled={exporting}
                  variant="outline"
                  className="rounded-sm border-slate-300 text-slate-700 hover:bg-slate-50 h-auto py-4 flex-col items-start"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="h-4 w-4" />
                    <span className="font-bold">Site Instructions</span>
                  </div>
                  <span className="text-xs opacity-70">{stats.totalSis} items</span>
                </Button>
                
                <Button
                  onClick={() => exportToCSV('vos')}
                  disabled={exporting}
                  variant="outline"
                  className="rounded-sm border-violet-300 text-violet-700 hover:bg-violet-50 h-auto py-4 flex-col items-start"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="h-4 w-4" />
                    <span className="font-bold">Variation Orders</span>
                  </div>
                  <span className="text-xs opacity-70">{stats.totalVos} items</span>
                </Button>

                <Button
                  onClick={() => exportToCSV('meetings')}
                  disabled={exporting}
                  variant="outline"
                  className="rounded-sm border-teal-300 text-teal-700 hover:bg-teal-50 h-auto py-4 flex-col items-start"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="h-4 w-4" />
                    <span className="font-bold">Meeting Minutes</span>
                  </div>
                  <span className="text-xs opacity-70">{stats.totalMeetings} items</span>
                </Button>

                <Button
                  onClick={() => exportToCSV('shop_drawings')}
                  disabled={exporting}
                  variant="outline"
                  className="rounded-sm border-indigo-300 text-indigo-700 hover:bg-indigo-50 h-auto py-4 flex-col items-start"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="h-4 w-4" />
                    <span className="font-bold">Shop Drawings</span>
                  </div>
                  <span className="text-xs opacity-70">{stats.totalShopDrawings} items</span>
                </Button>

                <Button
                  onClick={() => exportToCSV('procurement')}
                  disabled={exporting}
                  variant="outline"
                  className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-auto py-4 flex-col items-start"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="h-4 w-4" />
                    <span className="font-bold">Procurement</span>
                  </div>
                  <span className="text-xs opacity-70">{stats.totalProcurement} items</span>
                </Button>

                <Button
                  onClick={() => exportToCSV('irs')}
                  disabled={exporting}
                  variant="outline"
                  className="rounded-sm border-yellow-300 text-yellow-700 hover:bg-yellow-50 h-auto py-4 flex-col items-start"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="h-4 w-4" />
                    <span className="font-bold">IRS Items</span>
                  </div>
                  <span className="text-xs opacity-70">{stats.totalIrs} items</span>
                </Button>

                <Button
                  onClick={() => exportToCSV('subcontractors')}
                  disabled={exporting}
                  variant="outline"
                  className="rounded-sm border-stone-300 text-stone-700 hover:bg-stone-50 h-auto py-4 flex-col items-start"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="h-4 w-4" />
                    <span className="font-bold">Subcontractors</span>
                  </div>
                  <span className="text-xs opacity-70">{stats.totalSubcontractors} items</span>
                </Button>

                <Button
                  onClick={() => exportToCSV('drawings')}
                  disabled={exporting}
                  variant="outline"
                  className="rounded-sm border-sky-300 text-sky-700 hover:bg-sky-50 h-auto py-4 flex-col items-start"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="h-4 w-4" />
                    <span className="font-bold">Drawings</span>
                  </div>
                  <span className="text-xs opacity-70">{stats.totalDrawings} items</span>
                </Button>

                <Button
                  onClick={() => exportToCSV('tasks')}
                  disabled={exporting}
                  variant="outline"
                  className="rounded-sm border-rose-300 text-rose-700 hover:bg-rose-50 h-auto py-4 flex-col items-start"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <CheckSquare className="h-4 w-4" />
                    <span className="font-bold">Tasks</span>
                  </div>
                  <span className="text-xs opacity-70">{stats.totalTasks} items</span>
                </Button>

                <Button
                  onClick={() => exportToCSV('disputes')}
                  disabled={exporting}
                  variant="outline"
                  className="rounded-sm border-red-300 text-red-700 hover:bg-red-50 h-auto py-4 flex-col items-start"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Scale className="h-4 w-4" />
                    <span className="font-bold">Disputes</span>
                  </div>
                  <span className="text-xs opacity-70">{stats.totalDisputes} items</span>
                </Button>

                <Button
                  onClick={() => exportToCSV('closeout')}
                  disabled={exporting}
                  variant="outline"
                  className="rounded-sm border-green-300 text-green-700 hover:bg-green-50 h-auto py-4 flex-col items-start"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Shield className="h-4 w-4" />
                    <span className="font-bold">Close-Out</span>
                  </div>
                  <span className="text-xs opacity-70">{stats.totalCloseout} items</span>
                </Button>
              </div>

              {exporting && (
                <div className="mt-4 flex items-center gap-2 text-slate-500 text-sm">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Generating export...
                </div>
              )}
            </div>

            {/* Export Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
              <h4 className="font-bold text-sm text-blue-800 mb-2">Export Notes</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Filters (project, status, date range) are applied to exports</li>
                <li>• CSV format compatible with Excel, Google Sheets, and other tools</li>
                <li>• Large datasets may take a moment to generate</li>
              </ul>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
